// The running bundle's identity, stamped at build time by vite.config.ts.
//
// This exists because "is my browser running what I just deployed?" is not
// answerable by looking at the app, and getting it wrong is expensive: built
// assets are immutable-cached by design, so a tab that has not re-fetched
// index.html serves old JS indefinitely while the server is entirely correct.
// Twice that read as a missing feature and cost a full debugging round. The
// stamp is logged at startup and shown in the admin console, so the question
// takes one glance rather than a devtools session.

declare const __BUILD_SHA__: string;
declare const __BUILD_AT__: string;

export const BUILD_SHA: string =
  typeof __BUILD_SHA__ === 'string' ? __BUILD_SHA__ : 'dev';
export const BUILD_AT: string =
  typeof __BUILD_AT__ === 'string' ? __BUILD_AT__ : '';

/** "a1b2c3d · 30 Jul 05:12" — short enough for a status line. */
export function buildLabel(): string {
  if (!BUILD_AT) return BUILD_SHA;
  const when = new Date(BUILD_AT);
  return Number.isNaN(when.getTime())
    ? BUILD_SHA
    : `${BUILD_SHA} · ${when.toLocaleString(undefined, {
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`;
}

// --- deploy watcher ----------------------------------------------------------
// This is a monitoring cockpit: tabs stay open for days. index.html is
// no-cache but an open tab never re-fetches it, so a deploy silently never
// arrives — the operator watches a week-old bundle while the server is
// current, and every new feature "doesn't work". The stamp above made that
// diagnosable; this makes it self-announcing. Every 5 minutes, fetch
// index.html fresh, read which entry chunk it names, and compare with the
// chunk actually running (import.meta.url). Mismatch → an unmissable chip
// that reloads. Never auto-reload: the operator may be mid-replay or
// mid-edit, and a page that reloads itself under someone's hands is worse
// than a stale one.
const CHECK_MS = 5 * 60_000;
let offered = false;

export function watchForNewBuild(): void {
  const running = ((): string => {
    try {
      return new URL(import.meta.url).pathname.split('/').pop() ?? '';
    } catch {
      return '';
    }
  })();
  // Dev server modules aren't hashed chunks; there is nothing to watch.
  if (!/^index-[A-Za-z0-9_-]+\.js$/.test(running)) return;
  const check = async (): Promise<void> => {
    if (offered) return;
    try {
      const res = await fetch('/', { cache: 'no-store' });
      if (!res.ok) return;
      const html = await res.text();
      const m = html.match(/\/assets\/(index-[A-Za-z0-9_-]+\.js)/);
      if (m && m[1] !== running) offerReload();
    } catch { /* offline or mid-deploy — the next tick asks again */ }
  };
  window.setInterval(() => { void check(); }, CHECK_MS);
  window.setTimeout(() => { void check(); }, 45_000);
}

function offerReload(): void {
  if (offered) return;
  offered = true;
  const chip = document.createElement('button');
  chip.type = 'button';
  chip.className = 'build-reload';
  // Worded and styled as a WARNING about this tab, not a refresh control —
  // the first cut read as a UI button and was treated as one.
  chip.textContent = '⚠ This tab is outdated — click to load the new version';
  chip.title = 'A newer build has been deployed. This tab keeps running the '
    + 'old code until it reloads; nothing else brings the new version in.';
  chip.addEventListener('click', () => location.reload());
  document.body.appendChild(chip);
}
